import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logoutUsers } from '../../store/UserSlice';
import { ProfileDetails } from '../../store/ProfileSlice';
import { RootState, AppDispatch } from '../../store/store';
import { toast } from 'react-toastify';
import DropdownUser from '../../components/DropdownUser';

const UserSidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);
  const [imgError, setImgError] = useState(false);

  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const reduxProfile: any = useSelector((state: RootState) => state.profile.data);
  const reduxUser: any = useSelector((state: RootState) => state.user.user);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(ProfileDetails());
    }
  }, [dispatch, isLoggedIn]);

  const currentUser = useMemo(() => {
    let localLoginUser: any = null;
    let localProfile: any = null;
    try {
      localLoginUser = JSON.parse(localStorage.getItem('loginData') || '')?.user;
    } catch {
      localLoginUser = null;
    }
    try {
      localProfile = JSON.parse(localStorage.getItem('profile') || '');
    } catch {
      localProfile = null;
    }

    const reduxUserObj = reduxUser?.user || reduxUser;
    const reduxProfileObj = reduxProfile && !Array.isArray(reduxProfile) ? reduxProfile : null;

    return {
      ...(localLoginUser || {}),
      ...(localProfile || {}),
      ...(reduxUserObj && typeof reduxUserObj === 'object' ? reduxUserObj : {}),
      ...(reduxProfileObj || {}),
    };
  }, [reduxProfile, reduxUser]);

  const userName = currentUser?.fullName || currentUser?.name || "User";
  const userEmail = currentUser?.email || "";
  const userAvatar = currentUser?.icon || "";
  const isAdmin = currentUser?.isAdmin === true;

  const handleImageError = () => {
    setImgError(true);
    if (isLoggedIn) {
      dispatch(ProfileDetails());
    }
  };

  const handleLogout = () => {
    dispatch(logoutUsers()).then(() => {
      toast.success(t("nav.logoutSuccess", "Logged out Successfully."));
      navigate('/login');
    });
  };

  const openSidebar = () => {
    setIsSidebarHidden(!isSidebarHidden);
  };

  return (
    <>
      <div className="flex items-center justify-between md:hidden mb-2">
        <button
          onClick={openSidebar}
          className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-[#041B44] bg-white rounded-xl shadow-xs border border-gray-200 hover:bg-gray-50 focus:outline-none"
          aria-label="Open sidebar"
        >
          <svg className="w-5 h-5 text-[#037897]" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path clipRule="evenodd" fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"></path>
          </svg>
          <span>Menu</span>
        </button>
      </div>

      {/* Mobile Backdrop */}
      {isSidebarHidden && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[9998] md:hidden"
          onClick={() => setIsSidebarHidden(false)}
        />
      )}

      <aside
        className={`fixed top-0 left-0 bottom-0 z-[9999] md:z-auto md:static bg-white w-72 md:w-full h-full md:h-auto min-h-full md:min-h-0 overflow-y-auto scrollbar-hide shadow-2xl md:shadow-sm rounded-r-2xl md:rounded-2xl border border-gray-100 transition-transform duration-300 ease-in-out ${
          isSidebarHidden ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        id="cta-button-sidebar"
        aria-label="Sidebar"
      >
        {/* Mobile Header in Drawer */}
        <div className="flex md:hidden items-center justify-between p-4 border-b border-gray-100">
          <span className="font-bold text-gray-800">Navigation</span>
          <button
            onClick={() => setIsSidebarHidden(false)}
            className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            ✕
          </button>
        </div>

        <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear bg-white p-3">
          <nav>
            <ul className="flex flex-col space-y-1">
              {/* Profile Card Header */}
              <li className="profile-menu pb-4 border-b border-gray-100 mb-3">
                <div className="flex items-center gap-3 p-2 rounded-xl bg-gray-50/70 border border-gray-100">
                  <div className="h-11 w-11 rounded-full overflow-hidden shrink-0 border border-gray-200 bg-white flex items-center justify-center">
                    {userAvatar && !imgError ? (
                      <img
                        src={userAvatar}
                        alt="User"
                        className="h-full w-full object-cover"
                        onError={handleImageError}
                      />
                    ) : (
                      <img
                        src="/images/user/default-avatar.jpg"
                        alt="User"
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-[#041B44] truncate">
                      {userName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {userEmail || "Dashboard"}
                    </p>
                  </div>
                </div>
              </li>

              {/* Profile Link */}
              <li>
                <Link
                  to="/profile"
                  onClick={() => setIsSidebarHidden(false)}
                  className={`flex items-center gap-3 text-sm md:text-base font-semibold rounded-xl px-3.5 py-2.5 my-1 w-full group transition-all ${
                    location.pathname === '/profile'
                      ? 'text-white bg-[#037897] shadow-xs'
                      : 'text-[#455A64] hover:bg-[#037897] hover:text-white'
                  }`}
                >
                  <svg
                    className={`w-5 h-5 shrink-0 ${location.pathname === '/profile' ? 'stroke-white' : 'stroke-[#455A64] group-hover:stroke-white'}`}
                    viewBox="0 0 18 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="8.57897"
                      cy="5.77803"
                      r="4.77803"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M1.00002 17.2013C0.998732 16.8654 1.07385 16.5336 1.2197 16.2311C1.67736 15.3157 2.96798 14.8306 4.03892 14.6109C4.81128 14.4461 5.59431 14.336 6.38217 14.2814C7.84084 14.1533 9.30793 14.1533 10.7666 14.2814C11.5544 14.3366 12.3374 14.4467 13.1099 14.6109C14.1808 14.8306 15.4714 15.27 15.9291 16.2311C16.2224 16.8479 16.2224 17.5639 15.9291 18.1807C15.4714 19.1418 14.1808 19.5812 13.1099 19.7917C12.3384 19.9633 11.5551 20.0766 10.7666 20.1304C9.57937 20.231 8.38659 20.2494 7.19681 20.1853C6.92221 20.1853 6.65677 20.1853 6.38217 20.1304C5.59663 20.0772 4.81632 19.964 4.04807 19.7917C2.96798 19.5812 1.68652 19.1418 1.2197 18.1807C1.0746 17.8746 0.999552 17.54 1.00002 17.2013Z"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="whitespace-nowrap">{t("userSidebar.profile")}</span>
                </Link>
              </li>

              {/* My Bookings */}
              <li>
                <Link
                  to="/bookings"
                  onClick={() => setIsSidebarHidden(false)}
                  className={`flex items-center gap-3 text-sm md:text-base font-semibold rounded-xl px-3.5 py-2.5 my-1 w-full group transition-all ${
                    location.pathname.startsWith('/booking')
                      ? 'text-white bg-[#037897] shadow-xs'
                      : 'text-[#455A64] hover:bg-[#037897] hover:text-white'
                  }`}
                >
                  <svg
                    className={`w-5 h-5 shrink-0 ${location.pathname.startsWith('/booking') ? 'stroke-white fill-transparent' : 'stroke-[#455A64] group-hover:stroke-white fill-transparent'}`}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 2V6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 2V6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 10H21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 14H8.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 14H12.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 14H16.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="whitespace-nowrap">{t("userSidebar.myBookings")}</span>
                </Link>
              </li>

              {/* All Programs */}
              <li>
                <Link
                  to="/programs"
                  onClick={() => setIsSidebarHidden(false)}
                  className={`flex items-center gap-3 text-sm md:text-base font-semibold rounded-xl px-3.5 py-2.5 my-1 w-full group transition-all ${
                    location.pathname === '/programs'
                      ? 'text-white bg-[#037897] shadow-xs'
                      : 'text-[#455A64] hover:bg-[#037897] hover:text-white'
                  }`}
                >
                  <svg
                    className={`w-5 h-5 shrink-0 ${location.pathname === '/programs' ? 'stroke-white' : 'stroke-[#455A64] group-hover:stroke-white'}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 6h8M8 10h8M8 14h5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="whitespace-nowrap">{t("userSidebar.allPrograms")}</span>
                </Link>
              </li>

              {/* Admin Panel Link */}
              {isAdmin && (
                <li>
                  <Link
                    to="/admin/dashboard"
                    onClick={() => setIsSidebarHidden(false)}
                    className="flex items-center gap-3 text-sm md:text-base font-semibold rounded-xl px-3.5 py-2.5 my-1 w-full text-[#B4804A] hover:bg-[#B4804A] hover:text-white group transition-all"
                  >
                    <svg
                      className="w-5 h-5 shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="whitespace-nowrap">{t("nav.adminPanel", "Admin Panel")}</span>
                  </Link>
                </li>
              )}

              {/* Logout Button */}
              <li className="pt-2 border-t border-gray-100 mt-2">
                <button
                  type="button"
                  className="flex items-center gap-3 text-sm md:text-base font-semibold rounded-xl px-3.5 py-2.5 my-1 w-full text-left text-[#455A64] hover:bg-red-50 hover:text-red-600 group transition-all"
                  onClick={handleLogout}
                >
                  <svg
                    className="w-5 h-5 shrink-0 stroke-[#455A64] group-hover:stroke-red-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17L21 12L16 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12H9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span className="whitespace-nowrap">{t("userSidebar.logout")}</span>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </aside>
    </>
  );
};

export default UserSidebar;